/**
 * The board camera: which point of the plane it looks at sits at the centre of the viewport,
 * and how many CSS px one unit takes. The plane is the projection's view space (see
 * projection.ts); the camera itself knows nothing about cells or tilt. Lives outside React so wheel and drag events (60+ per second) only
 * move the scene; React reads it through `subscribe`/`getSnapshot` where it needs to.
 */

export interface CameraView {
  /** World point at the centre of the content area. */
  readonly x: number;
  readonly y: number;
  /** CSS px per world unit. */
  readonly zoom: number;
}

/** Screen-space bands kept free of the map's edge (rulers, minimap...), in CSS px. */
export interface Insets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface CameraOptions {
  /** Largest zoom, CSS px per world unit. */
  readonly maxZoom: number;
  readonly insets?: Insets;
  /** How far past the map's edge the camera may pan, CSS px. */
  readonly overscroll?: number;
}

type Listener = () => void;

const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
/** How fast a glide loses speed: the share of it kept after one ms (iOS scrolling: 0.998). */
const GLIDE_DECAY = 0.997;
/** A glide stops below this speed, CSS px per ms. */
const GLIDE_MIN_SPEED = 0.02;

export class Camera {
  private view: CameraView = { x: 0, y: 0, zoom: 1 };
  private viewport = { width: 0, height: 0 };
  /** The content's box on the plane; the map is kept in view around it. */
  private world = { x: 0, y: 0, width: 0, height: 0 };
  private readonly listeners = new Set<Listener>();
  private flight: number | null = null;
  /** Where the running flight ends, so repeated zoom clicks build on it. */
  private flightTarget: CameraView | null = null;
  private readonly maxZoom: number;
  private readonly insets: Insets;
  private readonly overscroll: number;
  private fitted = false;

  constructor(options: CameraOptions) {
    this.maxZoom = options.maxZoom;
    this.insets = options.insets ?? NO_INSETS;
    this.overscroll = options.overscroll ?? 48;
  }

  // -- reading ---------------------------------------------------------------

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Immutable: a new object after every change, so it works with useSyncExternalStore. */
  readonly getSnapshot = (): CameraView => this.view;

  get viewportSize(): { readonly width: number; readonly height: number } {
    return this.viewport;
  }

  /** The zoom at which the whole map fits the content area. */
  get minZoom(): number {
    const { w, h } = this.content();
    if (!this.world.width || !this.world.height || w <= 0 || h <= 0) return 1;
    return Math.min(w / this.world.width, h / this.world.height, this.maxZoom);
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const c = this.contentCentre();
    return { x: this.view.x + (sx - c.x) / this.view.zoom, y: this.view.y + (sy - c.y) / this.view.zoom };
  }

  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    const c = this.contentCentre();
    return { x: c.x + (wx - this.view.x) * this.view.zoom, y: c.y + (wy - this.view.y) * this.view.zoom };
  }

  // -- setup -----------------------------------------------------------------

  setViewport(width: number, height: number): void {
    if (width === this.viewport.width && height === this.viewport.height) return;
    this.viewport = { width, height };
    if (!this.fitted && this.world.width && width && height) this.fit();
    else this.commit(this.view);
  }

  /**
   * The content's box on the plane. The first time (or with `refit`) the camera fits it;
   * otherwise it keeps its zoom and only clamps, so the caller can re-centre.
   */
  setBounds(box: { x: number; y: number; width: number; height: number }, refit = false): void {
    const w = this.world;
    if (box.x === w.x && box.y === w.y && box.width === w.width && box.height === w.height && !refit) return;
    this.world = { ...box };
    if (refit) this.fitted = false;
    if (!this.fitted) {
      if (this.viewport.width && this.viewport.height) this.fit();
    } else this.commit(this.view);
  }

  // -- moving ----------------------------------------------------------------

  /** Zoom by `factor`, keeping the world point under (sx, sy) where it is. */
  zoomAt(sx: number, sy: number, factor: number): void {
    this.stopFlight();
    const before = this.screenToWorld(sx, sy);
    const zoom = this.clampZoom(this.view.zoom * factor);
    const c = this.contentCentre();
    this.commit({ zoom, x: before.x - (sx - c.x) / zoom, y: before.y - (sy - c.y) / zoom });
  }

  /** Zoom about the centre of the content area, animated. */
  zoomBy(factor: number): void {
    const zoom = this.clampZoom((this.flightTarget ?? this.view).zoom * factor);
    this.flyTo({ ...(this.flightTarget ?? this.view), zoom });
  }

  /** Move the scene by a screen-space delta (drag right: the map follows the pointer). */
  panBy(dx: number, dy: number): void {
    this.stopFlight();
    this.commit({ ...this.view, x: this.view.x - dx / this.view.zoom, y: this.view.y - dy / this.view.zoom });
  }

  centerOn(wx: number, wy: number, animate = false): void {
    const target = { ...this.view, x: wx, y: wy };
    if (animate) this.flyTo(target);
    else {
      this.stopFlight();
      this.commit(target);
    }
  }

  /** The whole map, centred. */
  fit(animate = false): void {
    this.fitted = true;
    const target = {
      x: this.world.x + this.world.width / 2,
      y: this.world.y + this.world.height / 2,
      zoom: this.minZoom,
    };
    if (animate) this.flyTo(target);
    else {
      this.stopFlight();
      this.commit(target);
    }
  }

  flyTo(target: CameraView, ms = 220): void {
    this.stopFlight();
    const from = this.view;
    const to = this.clamp({ ...target, zoom: this.clampZoom(target.zoom) });
    if (typeof requestAnimationFrame === 'undefined' || ms <= 0) {
      this.commit(to);
      return;
    }
    this.flightTarget = to;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      const k = easeOutCubic(t);
      // Interpolate the zoom geometrically so zooming in and out feel the same.
      const zoom = from.zoom * (to.zoom / from.zoom) ** k;
      this.commit({ x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k, zoom });
      if (t < 1) this.flight = requestAnimationFrame(tick);
      else {
        this.flight = null;
        this.flightTarget = null;
      }
    };
    this.flight = requestAnimationFrame(tick);
  }

  /**
   * Keep moving after a fling, `vx`/`vy` in CSS px per ms, slowing down until it stops or
   * runs into the map's edge. Any new move or press stops it.
   */
  glide(vx: number, vy: number): void {
    this.stopFlight();
    if (typeof requestAnimationFrame === 'undefined') return;
    let last = performance.now();
    const tick = (now: number) => {
      // A dropped frame or two moves the map on all the same; a stalled tab does not.
      const dt = Math.min(200, now - last);
      last = now;
      const before = this.view;
      const k = GLIDE_DECAY ** dt;
      // The distance covered over dt while the speed decays from v to v·k.
      const s = (1 - k) / -Math.log(GLIDE_DECAY);
      this.commit({ ...before, x: before.x - (vx * s) / before.zoom, y: before.y - (vy * s) / before.zoom });
      vx *= k;
      vy *= k;
      const stuck = this.view === before;
      if (stuck || Math.hypot(vx, vy) < GLIDE_MIN_SPEED) this.flight = null;
      else this.flight = requestAnimationFrame(tick);
    };
    this.flight = requestAnimationFrame(tick);
  }

  stopFlight(): void {
    if (this.flight !== null) cancelAnimationFrame(this.flight);
    this.flight = null;
    this.flightTarget = null;
  }

  // -- internals -------------------------------------------------------------

  private content() {
    const { top, right, bottom, left } = this.insets;
    return { w: this.viewport.width - left - right, h: this.viewport.height - top - bottom };
  }

  private contentCentre() {
    const { w, h } = this.content();
    return { x: this.insets.left + w / 2, y: this.insets.top + h / 2 };
  }

  private clampZoom(zoom: number): number {
    return Math.min(this.maxZoom, Math.max(this.minZoom, zoom));
  }

  /**
   * Keep the map on screen: when it is smaller than the content area it is centred on that
   * axis, otherwise its edges may come in by at most `overscroll` px.
   */
  private clamp(view: CameraView): CameraView {
    const { w, h } = this.content();
    const axis = (pos: number, start: number, len: number, screenLen: number) => {
      const half = screenLen / 2 / view.zoom;
      const slack = this.overscroll / view.zoom;
      if (len <= 2 * half) return start + len / 2;
      return Math.min(start + len - half + slack, Math.max(start + half - slack, pos));
    };
    const { x, y, width, height } = this.world;
    return { zoom: view.zoom, x: axis(view.x, x, width, w), y: axis(view.y, y, height, h) };
  }

  private commit(view: CameraView): void {
    const next = this.clamp(view);
    if (next.x === this.view.x && next.y === this.view.y && next.zoom === this.view.zoom) return;
    this.view = next;
    for (const l of this.listeners) l();
  }
}
